import * as React from 'react'
import axios, { AxiosError } from 'axios'
import { useStripe } from '@stripe/react-stripe-js'
import { useUser, useLocale, useTranslation } from 'hooks'
import { csrfToken } from 'helpers'
import { CartItem } from '../components/payments/WalletPaymentButton'
import type {
  Stripe,
  PaymentIntent,
  PaymentRequest,
  PaymentRequestUpdateDetailsStatus,
  PaymentRequestShippingOption,
  PaymentRequestShippingAddressEvent,
  PaymentRequestShippingOptionEvent,
  PaymentRequestPaymentMethodEvent
} from '@stripe/stripe-js/types'

export const useWalletPayment = (
  prepareForPayment: (() => void) | undefined,
  teardownFromPayment: (() => void) | undefined,
  cart: CartItem[],
  currencyCode: string
) => {
  const stripe = useStripe() as Stripe
  const { localiseUrl } = useLocale()
  const { t } = useTranslation()
  const { isLoggedIn } = useUser()

  const [hasCheckedPaymentAvailability, setHasCheckedPaymentAvailability] =
    React.useState(false)

  const [paymentRequest, setPaymentRequest] = React.useState<
    PaymentRequest | undefined
  >()
  const [isAvailable, setIsAvailable] = React.useState(false)
  const [availableMethod, setAvailableMethod] = React.useState<Record<
    string,
    boolean
  > | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [errorMsg, setErrorMsg] = React.useState<string | undefined>('')

  const shippingOptions = React.useRef<ShippingOption[]>([])
  const selectedShippingOption = React.useRef<string>('')
  const displayItems = React.useRef<CartItem[]>([])
  const totalFees = React.useRef<TotalFee[]>([])

  // turns cart items into a format stripe needs
  const convertToWalletPayDisplayItems = React.useCallback(
    (itemsArray: CartItem[]) => {
      return itemsArray.map((item) => ({
        label: item.ProductName,
        amount: parseFloat(item.ItemPrice) * 100
      }))
    },
    []
  )
  // turns fees into a format stripe needs
  const convertToWalletPayFeeItems = React.useCallback(() => {
    return totalFees.current.map(
      (total_fee: { translation_key: string; value: number }) => ({
        label: t(`shared.${total_fee.translation_key}`),
        amount: total_fee.value
      })
    )
  }, [])

  // turns shipping options into a format stripe needs
  const convertToWalletPayShippingOptions = React.useCallback(
    (shippingOptionArray: ShippingOption[]) => {
      return shippingOptionArray.map((option) => {
        const shipping_label = t(
          `react_components.wallet_pay.${option.translation_key}_total`
        )

        return {
          id: option.id,
          label: shipping_label,
          amount: option.amount,
          detail: option.detail
        }
      })
    },
    [t]
  )

  const fetchEventUpdate = () => {
    const convertedDisplayItems = convertToWalletPayDisplayItems(
      displayItems.current
    )
    let runningTotal = convertedDisplayItems.reduce(
      (sum, item) => sum + item.amount,
      0
    )
    const totalShipping =
      shippingOptions.current?.find(
        (o) => o.id === selectedShippingOption.current
      )?.amount || 0

    runningTotal = runningTotal + totalShipping

    return {
      status: 'success' as PaymentRequestUpdateDetailsStatus,
      shippingOptions: convertToWalletPayShippingOptions(
        shippingOptions.current
      ) as PaymentRequestShippingOption[],
      displayItems: [
        ...convertedDisplayItems,
        ...convertToWalletPayFeeItems(),
        {
          label: t(`react_components.wallet_pay.shipping_total`),
          amount: totalShipping
        }
      ],
      total: {
        label: t('react_components.wallet_pay.items_total'),
        amount: runningTotal
      }
    }
  }

  // The shippingoptionchange event is emitted from a PaymentRequest whenever the customer selects a new shipping option.
  // By changing the option we update the shipping fee and total cost
  const shippingOptionChangeHandler = React.useCallback(
    async (event: PaymentRequestShippingOptionEvent) => {
      selectedShippingOption.current = event.shippingOption.id
      event.updateWith(fetchEventUpdate())
    },
    [fetchEventUpdate, t]
  )

  // The shippingaddresschange event is emitted from a PaymentRequest whenever the customer selects a new address in the browser's payment interface.
  // By changing the address we update the shipping fee and total cost
  const shippingAddressChangeHandler = React.useCallback(
    async (event: PaymentRequestShippingAddressEvent) => {
      await axios
        .get(localiseUrl('/api/shipping/cart_fees', { localeAsParam: true }), {
          params: {
            country_code: event?.shippingAddress?.country,
            city_name: event?.shippingAddress?.city,
            postal_code: event?.shippingAddress?.postalCode
          }
        })
        .then(({ data }) => {
          shippingOptions.current = data.shipping_options
          displayItems.current = data.items
          totalFees.current = data.total_fees
          let shippingOption = selectedShippingOption.current

          if (
            !selectedShippingOption.current ||
            !data.shipping_options.find(
              (o) => o.id === selectedShippingOption.current
            )
          ) {
            shippingOption = data.shipping_options[0].id
            selectedShippingOption.current = shippingOption
          }

          event.updateWith(fetchEventUpdate())
        })
        .catch((error) => {
          let axiosErrorMsg = ''

          if (axios.isAxiosError(error)) {
            const responseData = (error as AxiosError).response?.data

            if ((responseData as any)?.errors) {
              axiosErrorMsg = (responseData as any).errors
            }

            if (error.status === 422 || error.response?.status === 422) {
              event.updateWith({ status: 'invalid_shipping_address' })
              setErrorMsg(axiosErrorMsg)
              return
            }
          }

          // any other error, close modal and print error
          setErrorMsg(axiosErrorMsg || error.message)
          handlePaymentCancel()
        })
    },
    [localiseUrl, fetchEventUpdate, t]
  )

  const processOrder = React.useCallback(
    async (
      ev: PaymentRequestPaymentMethodEvent,
      paymentIntent: PaymentIntent
    ) => {
      // TODO: Eventually use the same endpoint. remove isLoggedIn?
      const apiUrl = isLoggedIn
        ? '/checkout/create-order.json'
        : '/checkout/guest/create-order.json'

      await axios
        .post(localiseUrl(apiUrl, { localeAsParam: true }), {
          authenticity_token: csrfToken,
          ev,
          paymentIntent
        })
        .then(({ data }) => {
          let url

          if (data.passed) {
            url = localiseUrl(`/account/orders/${data.order.id}/processing`)
            window.location.href = escape(encodeURI(url))
          } else {
            ev.complete('fail')
            setErrorMsg(data.message)
            handlePaymentCancel()
          }
        })
        .catch((error) => {
          ev.complete('fail')
          setErrorMsg(error.message)
          handlePaymentCancel()
        })
    },
    [isLoggedIn, localiseUrl]
  )

  const paymentMethodHandler = React.useCallback(
    async (ev: PaymentRequestPaymentMethodEvent) => {
      // Creates a payment intent, it will also validate the cart items
      const apiUrl = isLoggedIn
        ? '/checkout/payment-intent.json'
        : '/checkout/guest/payment-intent.json'

      const {
        data: { clientSecret, message }
      } = await axios.post(localiseUrl(apiUrl, { localeAsParam: true }), {
        authenticity_token: csrfToken,
        ev
      })

      // if the items in the cart aren't valid fail the payment
      if (message) {
        ev.complete('fail')
        setErrorMsg(message)
        const url = localiseUrl('/cart?items_invalid=true')

        window.location.href = url
        return
      }

      // Use stripe.confirmCardPayment when the customer submits your payment form.
      // When called, it will confirm the PaymentIntent with data you provide and carry out 3DS or other next actions if they are required.

      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        )

      if (confirmError) {
        ev.complete('fail')
        setErrorMsg(confirmError.message)
        setLoading(false)
        if (teardownFromPayment) teardownFromPayment()
      } else {
        ev.complete('success')

        // It checks if the PaymentIntent requires any actions and if so Stripe.js handles the flow.
        if (paymentIntent.status === 'requires_action') {
          // Let Stripe.js handle the rest of the payment flow.
          const { error } = await stripe.confirmCardPayment(clientSecret)

          if (error) {
            setErrorMsg(error.message)
          } else {
            processOrder(ev, paymentIntent)
          }
        } else {
          processOrder(ev, paymentIntent)
        }
      }
    },
    [isLoggedIn, localiseUrl, processOrder, stripe, teardownFromPayment]
  )

  const handlePaymentButtonClick = () => {
    // prepareForPayment will be available only on PDP, it will add the items to cart and track actions
    if (prepareForPayment) prepareForPayment()
    if (paymentRequest) paymentRequest.show()
    setLoading(true)
  }

  const handlePaymentCancel = React.useCallback(() => {
    setLoading(false)

    // teardownFromPayment will be available only on PDP
    // it will remove the items from cart if wallet payment gets cancelled (e.g. user closes the UI)
    if (teardownFromPayment) teardownFromPayment()
  }, [teardownFromPayment])

  const calculateInitialTotalPriceInCents = cart.reduce(
    (total, item) => total + parseFloat(item.ItemPrice) * 100,
    0
  )

  React.useEffect(() => {
    if (!stripe || hasCheckedPaymentAvailability) return
    setHasCheckedPaymentAvailability(true)

    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: currencyCode.toLowerCase(),
      total: {
        label: t('react_components.wallet_pay.items_total'),
        amount: calculateInitialTotalPriceInCents
      },
      displayItems: convertToWalletPayDisplayItems(cart),
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
      requestShipping: true
    })

    const checkPaymentAvailability = async () => {
      try {
        // check if wallet payment is available
        const result = await pr.canMakePayment()

        if (result) {
          setPaymentRequest(pr)
          setAvailableMethod(result)

          // If adding any other providers we need to ensure we track their `cancel` or `close` on the popup
          // Link in particular seems to bypass the `cancel` below.
          setIsAvailable(result.applePay || result.googlePay)
        } else {
          setIsAvailable(false)
        }
      } finally {
        setLoading(false)
      }
    }

    checkPaymentAvailability()
  }, [
    calculateInitialTotalPriceInCents,
    cart,
    convertToWalletPayDisplayItems,
    currencyCode,
    handlePaymentCancel,
    hasCheckedPaymentAvailability,
    paymentMethodHandler,
    paymentRequest,
    shippingAddressChangeHandler,
    stripe,
    t
  ])

  React.useEffect(() => {
    if (paymentRequest) {
      paymentRequest.on('shippingaddresschange', shippingAddressChangeHandler)
      paymentRequest.on('shippingoptionchange', shippingOptionChangeHandler)
      paymentRequest.on('paymentmethod', paymentMethodHandler)
      paymentRequest.on('cancel', handlePaymentCancel)
    }

    return () => {
      if (paymentRequest) {
        paymentRequest.off(
          'shippingaddresschange',
          shippingAddressChangeHandler
        )
        paymentRequest.off('shippingoptionchange', shippingOptionChangeHandler)
        paymentRequest.off('paymentmethod', paymentMethodHandler)
        paymentRequest.off('cancel', handlePaymentCancel)
      }
    }
  }, [
    paymentRequest,
    handlePaymentCancel,
    paymentMethodHandler,
    shippingAddressChangeHandler,
    shippingOptionChangeHandler
  ])

  return {
    handlePaymentButtonClick,
    errorMsg,
    loading,
    isAvailable,
    availableMethod,
    paymentRequest
  }
}

interface ShippingOption {
  id: string
  translation_key: string
  amount: number
  detail: string
}
interface TotalFee {
  translation_key: string
  value: number
}
